import * as React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { isChunkLoadError } from "@/lib/chunkRecovery";
// NOTE: ``reportErrorToFelix`` is intentionally lazy-imported in
// ``componentDidCatch`` rather than statically imported here. A
// static import would drag the entire FelixChat module graph
// (including theme.ts → window.matchMedia()) into every consumer
// of ErrorBoundary at module-load time. That broke jsdom-based
// tests of pages that wrap their content in ErrorBoundary
// (Dashboard.test.tsx, 2026-05-08 CI failure on
// fix/dashboard-flicker). Production behavior is unchanged —
// the dynamic import resolves once at first error and caches.

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error details (only in development to avoid leaking info in production)
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary] Caught an error:", error);
      console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    }

    // KI-0006: auto-reload for stale Vite chunk errors.  If we haven't
    // already reloaded within the last AUTO_RELOAD_GUARD_MS window, fire
    // a reload immediately so the user never sees the technical stack.
    // The guard prevents reload loops when the chunk failure is caused
    // by a real bug rather than a stale deploy.
    if (this.isChunkLoadErrorFrom(error) && this.canAutoReload()) {
      sessionStorage.setItem("_vite_reload_ts", String(Date.now()));
      window.location.reload();
      return;
    }

    // Surface the error to Felix so he can explain it to the user.
    // Lazy-imported (see file-header note) so consumers of
    // ErrorBoundary don't pay the FelixChat module-graph cost at
    // import time.
    void import("@/components/FelixChat")
      .then((mod) => {
        try {
          mod.reportErrorToFelix(error.message, "component");
        } catch {
          /* report failure is non-critical */
        }
      })
      .catch(() => {
        /* dynamic import failure is non-critical */
      });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private canAutoReload(): boolean {
    // Don't auto-reload if we just did (prevents infinite loop if the new
    // bundle also fails to load for a real reason).
    const AUTO_RELOAD_GUARD_MS = 60_000;
    try {
      const last = Number(sessionStorage.getItem("_vite_reload_ts")) || 0;
      return Date.now() - last > AUTO_RELOAD_GUARD_MS;
    } catch {
      return false;
    }
  }

  private isChunkLoadErrorFrom(error: Error | null): boolean {
    // One definition with the route-level recovery (row 75a2edb9). It also
    // knows Vite's "Unable to preload CSS", which used to fall through to the
    // generic "Something went wrong" page whose Try Again re-renders the same
    // cached failure; a download failure is now always offered a reload.
    return isChunkLoadError(error);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /** FB-0001/KI-0006: Detect stale Vite chunk errors */
  private isChunkLoadError(): boolean {
    return this.isChunkLoadErrorFrom(this.state.error);
  }

  handleReload = (): void => {
    sessionStorage.setItem("_vite_reload_ts", String(Date.now()));
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // FB-0001/KI-0006: Show a friendly message for stale asset errors
      if (this.isChunkLoadError()) {
        return (
          <div
            className={cn(
              "flex min-h-[400px] items-center justify-center p-8",
              this.props.className
            )}
          >
            <div className="mx-auto max-w-md text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30">
                <RefreshCw className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
                New version available
              </h2>
              <p className="mb-6 text-sm text-slate-600 dark:text-slate-300">
                A newer version of the application has been deployed. Please
                reload to get the latest version.
              </p>
              <Button onClick={this.handleReload} variant="default">
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Now
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div
          className={cn(
            "flex min-h-[400px] items-center justify-center p-8",
            this.props.className
          )}
        >
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error-bg">
              <AlertTriangle className="h-8 w-8 text-error" />
            </div>

            <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
              Something went wrong
            </h2>
            <p className="mb-6 text-sm text-slate-600 dark:text-slate-300">
              An unexpected error occurred. Please try again. If the problem
              persists, contact support.
            </p>

            {this.state.error && (
              <details className="mb-6 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 p-4 text-left">
                <summary className="cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100">
                  Error details
                </summary>
                <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap text-xs text-error">
                  {this.state.error.message}
                  {this.state.errorInfo?.componentStack && (
                    <>
                      {"\n\nComponent Stack:"}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <Button onClick={this.handleRetry} variant="default">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
export type { ErrorBoundaryProps };
